import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonAi } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  chatId: string
  cardStackId: string
  lessonTitle: string
  planbookId: string
}

export default function prepareLessonAiOpen(payload: Payload): PreparedAction {
  return {
    name: "LESSON_AI_OPEN",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let primaryPossessive = getPossessiveForPlanbook(findFn, userId, models.planbook?.id)
  let context = defaultLessonContext(payload, models)
  let newContext = assign({}, context, { chatId: payload.chatId })
  let newEvent: LessonAi = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,

    primary_verb: "use",
    primary_possessive: primaryPossessive,
    primary_object_type: "lesson",
    primary_object_specifier: "ai",

    adverbial_verb: "opening",
    adverbial_object_type: "chat",
    adverbial_object_specifier: null,
    adverbial_object_id: payload.chatId,
    adverbial_object_id_and_ancestor_ids: [...models.cardStackIdAndAncestorIds, payload.chatId],
  }

  return {
    title: "Opened AI chat",
    titleForAnalytics: "Lesson - AI Open Chat",
    description: `Opened AI chat for the ${payload.lessonTitle} lesson from ${models.course?.attributes.title}`,
    activity: defaultLessonActivity(payload, models),
    context: newContext,
    newAnalyticsEvents: [newEvent],
  }
}
